import { z } from 'zod';

import { type PermissionCode } from '../rbac/index.js';
import { REFUSAL_COMMENT_MAX_LENGTH, RequestStatus } from './requests/status.js';
import { RussiaRequestRefusalReasonSchema } from './russia-requests.js';

/**
 * @aiPurpose static state-machine for the Russia request capsule.
 *   Mirror of `REQUEST_TRANSITIONS` (China side). #1544 brings the
 *   Russia lifecycle to China parity: `send` stages the row in
 *   READY_FOR_WORK (the first carrier add atomically promotes it to
 *   IN_WORK, emitted as TRANSITION_AUTO_START from the carriers service —
 *   NOT a matrix transition); `process` gates on >= 1 ANSWERED carrier;
 *   `reopen` (PROCESSED -> IN_WORK), `success` (PROCESSED -> SUCCESS),
 *   and `refuse` (PROCESSED -> REFUSED) are the terminal-outcome moves;
 *   archive accepts every terminal status (CLOSED / SUCCESS / REFUSED).
 * @aiInvariant gating is the controller-level
 *   `@RequiresPermission('russiaRequests.transition.<key>')` check.
 *   Russia rows have no auto-assignment / round-robin, so there is no
 *   party gate equivalent to the China-side `TRANSITION_SIDE` matrix;
 *   permission + visibility (`RussiaRequestsVisibility`) together
 *   suffice. Append-only — historical audit rows reference these
 *   literals forever.
 */
export const RUSSIA_REQUEST_TRANSITIONS = {
  // WHY (#1544): `send` now stages the request in READY_FOR_WORK instead
  //   of jumping straight to IN_WORK. The first carrier addition is the
  //   atomic IN_WORK transition (emitted as TRANSITION_AUTO_START from
  //   `RussiaRequestCarriersService.addCarrier`). `inWorkAt` is still
  //   stamped on send so the hand-over timestamp begins before a logist
  //   attaches the first carrier. send stays STATUS-ONLY (no positions /
  //   totals gate — cargo strictness is #1545/#1546).
  send: {
    from: [RequestStatus.DRAFT, RequestStatus.RETURN],
    to: RequestStatus.READY_FOR_WORK,
    permissions: ['russiaRequests.transition.send'] as readonly PermissionCode[],
  },
  return: {
    from: [RequestStatus.IN_WORK],
    to: RequestStatus.RETURN,
    permissions: ['russiaRequests.transition.return'] as readonly PermissionCode[],
    requireReason: true,
  },
  // WHY (#1544): `process` gates on >= 1 carrier in `ANSWERED` state
  //   (mirror of China `requiresAnsweredCarrier`). The gate is enforced
  //   in the transition service mutate callback.
  process: {
    from: [RequestStatus.IN_WORK],
    to: RequestStatus.PROCESSED,
    permissions: ['russiaRequests.transition.process'] as readonly PermissionCode[],
    requiresAnsweredCarrier: true,
  },
  // WHY (#1544): logist_ru-side reopen of a PROCESSED row back to IN_WORK
  //   when more logistics work is needed. Clears `winnerBidId`, stamps
  //   `reopenedAt`, increments `reopenedCount`. Reason is required.
  reopen: {
    from: [RequestStatus.PROCESSED],
    to: RequestStatus.IN_WORK,
    permissions: ['russiaRequests.transition.reopen'] as readonly PermissionCode[],
    requireReason: true,
  },
  close: {
    from: [RequestStatus.PROCESSED],
    to: RequestStatus.CLOSED,
    permissions: ['russiaRequests.transition.close'] as readonly PermissionCode[],
  },
  // WHY (#1544): manager-side finalisation when the customer accepts the
  //   logistics quote. Requires a non-null stored `winnerBidId`; the
  //   service re-validates the stored bid's membership + carrier-state
  //   ANSWERED at COMMIT.
  // WHY (#1837): source statuses widened to revive a parked/refused row.
  //   From REFUSED (winner null) or POSTPONED-without-winner the manager
  //   re-picks the winner first (on the carriers tab); leaving REFUSED also
  //   clears the refusal fields at commit. `requiresWinner` stays
  //   authoritative at commit for every source status.
  success: {
    from: [RequestStatus.PROCESSED, RequestStatus.REFUSED, RequestStatus.POSTPONED],
    to: RequestStatus.SUCCESS,
    permissions: ['russiaRequests.transition.success'] as readonly PermissionCode[],
    requiresWinner: true,
  },
  // WHY (#1544): manager-side finalisation when the customer refuses.
  //   Requires a structured `RussiaRequestRefusalReason`; the optional
  //   comment is trimmed + bounded at the wire layer and stored on the
  //   row only (never duplicated into audit ledger payload).
  // WHY (#1837): POSTPONED added as a legal source so a parked request can
  //   be refused directly; behaviour is identical to PROCESSED -> REFUSED.
  refuse: {
    from: [RequestStatus.PROCESSED, RequestStatus.POSTPONED],
    to: RequestStatus.REFUSED,
    permissions: ['russiaRequests.transition.refuse'] as readonly PermissionCode[],
    requireRefusalReason: true,
  },
  // WHY (#1837): manager-side parking of a processed quote when the
  //   customer's decision is pending. POSTPONED is active/non-terminal —
  //   `closedAt` stays null and any stored `winnerBidId` is preserved, so
  //   the request behaves like PROCESSED while parked. Revive via `success`
  //   (re-pick if needed) or `refuse`. Russia has no party-side gate; the
  //   controller `@RequiresPermission('russiaRequests.transition.postpone')`
  //   plus visibility are the authority.
  postpone: {
    from: [RequestStatus.PROCESSED],
    to: RequestStatus.POSTPONED,
    permissions: ['russiaRequests.transition.postpone'] as readonly PermissionCode[],
  },
  // WHY (#1544): archive now accepts every terminal status (CLOSED,
  //   SUCCESS, REFUSED). The service snapshots `row.status` into
  //   `archivedFromStatus` so unarchive restores it instead of collapsing
  //   to CLOSED.
  archive: {
    from: [RequestStatus.CLOSED, RequestStatus.SUCCESS, RequestStatus.REFUSED],
    to: RequestStatus.ARCHIVE,
    permissions: ['russiaRequests.transition.archive'] as readonly PermissionCode[],
  },
  // WHY (#705 Slice 2c): `to: CLOSED` is the matrix-level fence and the
  //   legacy null-archivedFromStatus fallback target. The service
  //   restores `archivedFromStatus` when present.
  unarchive: {
    from: [RequestStatus.ARCHIVE],
    to: RequestStatus.CLOSED,
    permissions: ['russiaRequests.transition.unarchive'] as readonly PermissionCode[],
  },
} as const;

/**
 * @aiPurpose union of every Russia transition name.
 * @aiInvariant equals the keys of `RUSSIA_REQUEST_TRANSITIONS` at the
 *   type level.
 */
export type RussiaRequestTransitionKey = keyof typeof RUSSIA_REQUEST_TRANSITIONS;

/**
 * @aiPurpose POST /russia-requests/:id/transitions/return body. `reason`
 *   is intentionally optional at the wire layer so a missing/empty reason
 *   surfaces the typed `REASON_REQUIRED` error code from the service
 *   (mirror of the China `ReturnTransitionBody` semantics — required-at-
 *   schema would collapse to the generic `VALIDATION_ERROR`).
 */
export const ReturnRussiaRequestBodySchema = z
  .object({
    reason: z.string().max(2000).optional(),
  })
  .strict();
export type ReturnRussiaRequestBody = z.infer<typeof ReturnRussiaRequestBodySchema>;

/**
 * @aiPurpose POST /russia-requests/:id/transitions/reopen body. Mirror of
 *   the China `ReturnTransitionBody` semantics — `reason` is optional at
 *   the wire layer so a missing/empty reason surfaces the typed
 *   `REASON_REQUIRED` code from the service rather than a generic
 *   `VALIDATION_ERROR`.
 */
export const ReopenRussiaRequestBodySchema = z
  .object({
    reason: z.string().max(2000).optional(),
  })
  .strict();
export type ReopenRussiaRequestBody = z.infer<typeof ReopenRussiaRequestBodySchema>;

/**
 * @aiPurpose POST /russia-requests/:id/winner body — manager-side
 *   winner-bid selection (#1544). `bidId` is the UUID of the winning
 *   `RussiaRequestCarrierBid` row. The service verifies the bid belongs
 *   to the request and its carrier is in `ANSWERED` state at COMMIT.
 * @aiSecurity gates HIGH-RISK manager-side commercial decision; require
 *   `russiaRequests.manage` AND the manager-ownership (or admin) party
 *   gate. `logist_ru` is rejected (403).
 */
export const PickRussiaRequestWinnerBodySchema = z
  .object({
    bidId: z.string().uuid(),
  })
  .strict();
export type PickRussiaRequestWinnerBody = z.infer<typeof PickRussiaRequestWinnerBodySchema>;

/**
 * @aiPurpose POST /russia-requests/:id/transitions/success body (#1544).
 *   The canonical winner is the stored `RussiaRequest.winnerBidId`. The
 *   body `bidId` is optional and back-compat only — when present it must
 *   equal the stored winner or the request is rejected with the typed
 *   `WINNER_REQUIRED` gate code.
 * @aiSecurity gates HIGH-RISK manager-side finalisation; require both
 *   `russiaRequests.transition.success` AND the manager-ownership gate.
 */
// WHY (#1544): `.default({})` lets the ZodValidationPipe accept an absent
//   body — the web client posts none (winner already stored via /winner).
//   `.strict()` still rejects unknown keys; `bidId` still validates as UUID.
export const SuccessRussiaRequestBodySchema = z
  .object({
    bidId: z.string().uuid().optional(),
  })
  .strict()
  .default({});
export type SuccessRussiaRequestBody = z.infer<typeof SuccessRussiaRequestBodySchema>;

/**
 * @aiPurpose POST /russia-requests/:id/transitions/refuse body (#1544).
 *   `reason` is a stable machine code (`RussiaRequestRefusalReason`). The
 *   optional `comment` is trimmed at the service layer; empty-after-trim
 *   normalises to null. The raw comment lives ONLY on
 *   `RussiaRequest.refusalComment` and is never duplicated into the audit
 *   ledger payload (a `hasRefusalComment` boolean may surface there).
 * @aiSecurity gates HIGH-RISK manager-side finalisation; require both
 *   `russiaRequests.transition.refuse` AND the manager-ownership gate.
 * @aiPii `comment` is free text and may contain PII — bounded and
 *   redacted in logs by the recordMutation pipeline.
 */
export const RefuseRussiaRequestBodySchema = z
  .object({
    reason: RussiaRequestRefusalReasonSchema,
    comment: z.string().max(REFUSAL_COMMENT_MAX_LENGTH).optional(),
  })
  .strict();
export type RefuseRussiaRequestBody = z.infer<typeof RefuseRussiaRequestBodySchema>;
